import { formatDustDuration } from './dustDurationFormat';
import type { WorkHistoryEntryDraft } from './careerHistorySurveyTypes';

function formatIsoDate(iso: string, locale: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function buildWorkHistoryEntrySummary(
  draft: WorkHistoryEntryDraft,
  t: (key: string) => string,
  locale: string
): string {
  const lines: string[] = [];
  const push = (labelKey: string, value: string) => {
    if (!value.trim()) return;
    lines.push(`${t(labelKey)}: ${value.trim()}`);
  };

  push('submitDocuments.careerHistorySurvey.summary.siteName', draft.siteName);
  push('submitDocuments.careerHistorySurvey.summary.location', draft.location);
  push('submitDocuments.careerHistorySurvey.summary.contractor', draft.contractor);
  push('submitDocuments.careerHistorySurvey.summary.companyName', draft.companyName);
  push('submitDocuments.careerHistorySurvey.summary.incharge', draft.incharge);
  push('submitDocuments.careerHistorySurvey.summary.workPerformed', draft.workPerformed);
  if (draft.startDate) {
    push(
      'submitDocuments.careerHistorySurvey.summary.startDate',
      formatIsoDate(draft.startDate, locale)
    );
  }
  if (draft.endDateSkipped) {
    lines.push(`${t('submitDocuments.careerHistorySurvey.summary.endDate')}: ${t('submitDocuments.careerHistorySurvey.currentJob')}`);
  } else if (draft.endDate) {
    push(
      'submitDocuments.careerHistorySurvey.summary.endDate',
      formatIsoDate(draft.endDate, locale)
    );
  }
  if (!draft.dustDurationSkipped) {
    const text =
      draft.dustDuration.trim() ||
      formatDustDuration(draft.dustDurationYears, draft.dustDurationMonths, locale);
    if (text) push('submitDocuments.careerHistorySurvey.summary.dustDuration', text);
  }
  return lines.join('\n');
}

export function userAnswerForEntryStep(
  step: keyof WorkHistoryEntryDraft | 'entrySummary',
  draft: WorkHistoryEntryDraft,
  t: (key: string) => string,
  locale: string
): string {
  switch (step) {
    case 'siteName':
      return draft.siteName.trim();
    case 'location':
      return draft.location.trim();
    case 'contractor':
      return draft.contractor.trim();
    case 'companyName':
      return draft.companyName.trim();
    case 'incharge':
      return draft.incharge.trim();
    case 'workPerformed':
      return draft.workPerformed.trim();
    case 'startDate':
      return draft.startDate ? formatIsoDate(draft.startDate, locale) : '';
    case 'endDate':
      if (draft.endDateSkipped) return t('submitDocuments.careerHistorySurvey.currentJob');
      return draft.endDate ? formatIsoDate(draft.endDate, locale) : '';
    case 'dustDuration':
      if (draft.dustDurationSkipped) return t('submitDocuments.careerHistorySurvey.notApplicable');
      if (draft.dustDuration.trim()) return draft.dustDuration.trim();
      return formatDustDuration(draft.dustDurationYears, draft.dustDurationMonths, locale);
    case 'entrySummary':
      return buildWorkHistoryEntrySummary(draft, t, locale);
    default:
      return '';
  }
}
